import { ChangeDetectionStrategy, Component, inject, Input, Signal } from '@angular/core';
import {
  ActionModalComponent,
  AppRoutes,
  COPY_TOKEN,
  CopyProvider,
  DEEP_LINK_TOKEN,
  DeepLinkProvider,
  MODAL_TOKEN,
  ModalProvider,
  TeamsRoutes,
  TOAST_TOKEN,
  ToastProvider
} from '@core';
import { Router } from '@angular/router';
import { Share } from '@capacitor/share';
import {
  GetTeamDetailsQueryResult,
  LeaveTeamCommandHandler,
  RemoveTeamCommandHandler
} from '../../application/handlers';
import { switchMap, tap } from 'rxjs';
import { TeamsService } from '@teams/infra';
import { inviteTeamText } from '../../application/utils';
import { FeatureFlag, FeatureFlagHandler } from '@feature-flags';
import { toSignal } from '@angular/core/rxjs-interop';
import { OrIconComponent } from '@ui-components';

@Component({
  selector: 'lib-manage-team',
  templateUrl: './manage-team.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [OrIconComponent],
  providers: [LeaveTeamCommandHandler, TeamsService, RemoveTeamCommandHandler]
})
export class ManageTeamComponent {
  private readonly router: Router = inject(Router);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly leaveTeamCommandHandler: LeaveTeamCommandHandler = inject(LeaveTeamCommandHandler);
  private readonly copyProvider: CopyProvider = inject(COPY_TOKEN);
  private readonly deepLinkProvider: DeepLinkProvider = inject(DEEP_LINK_TOKEN);
  private readonly removeTeamCommandHandler: RemoveTeamCommandHandler = inject(RemoveTeamCommandHandler);
  private readonly featureFlagHandler: FeatureFlagHandler = inject(FeatureFlagHandler);
  private readonly toastProvider: ToastProvider = inject(TOAST_TOKEN);

  // Modal component - must use @Input()
  @Input() team!: GetTeamDetailsQueryResult;

  readonly removeTeamFlag: Signal<boolean | undefined> = toSignal(
    this.featureFlagHandler.isEnabled$(FeatureFlag.REMOVE_TEAM)
  );

  editTeam(): void {
    this.modalProvider.dismissAllStoredModals();
    this.router.navigateByUrl(`${AppRoutes.TEAMS}/${this.team.teamId}/${TeamsRoutes.EDIT}/${this.team.teamName}`);
  }

  addMember(): void {
    this.modalProvider.dismissAllStoredModals();
    this.router.navigateByUrl(
      `${AppRoutes.TEAMS}/${this.team.teamId}/${this.team.invitationToken}/${TeamsRoutes.INVITE}`
    );
  }

  advancedShare(): void {
    const url: string = this.generateTeamUrl();

    Share.share({
      text: inviteTeamText(url),
      url: url
    });

    this.modalProvider.dismissModal$().subscribe();
  }

  copyTeamUrl(): void {
    const url: string = this.generateTeamUrl();

    this.copyProvider
      .copyToClipboard(inviteTeamText(url))
      .pipe(switchMap(() => this.modalProvider.dismissModal$()))
      .subscribe();
  }

  leaveTeam(): void {
    this.leaveTeamCommandHandler
      .leave(this.team.teamId)
      .pipe(
        tap(() => this.modalProvider.dismissAllStoredModals()),
        switchMap(() => this.router.navigateByUrl(AppRoutes.DASHBOARD))
      )
      .subscribe(() => this.toastProvider.showToast({ message: 'Opuszczono grupkę' }));
  }

  removeTeam(): void {
    this.modalProvider.showModal$({
      component: ActionModalComponent,
      cssClass: 'present-modal',
      componentProps: {
        header: 'Usuwanie grupki',
        message: 'Jesteś pewien, że chcesz usunąć grupke?',
        btnOk: 'Tak, potwierdzam',
        btnCancel: 'Anuluj',
        action: () => this.remove()
      }
    });
  }

  remove(): void {
    this.removeTeamCommandHandler
      .remove(this.team.teamId)
      .pipe(
        tap(() => this.modalProvider.dismissAllStoredModals()),
        switchMap(() => this.router.navigateByUrl(AppRoutes.DASHBOARD))
      )
      .subscribe(() => this.toastProvider.showToast({ message: 'Usunięto grupkę' }));
  }

  private generateTeamUrl(): string {
    const path: string = `${AppRoutes.TEAMS}/${TeamsRoutes.ACCEPT_INVITATION}/${this.team.teamId}/${this.team.invitationToken}`;

    return this.deepLinkProvider.generateWebLink(path);
  }
}
